'use client';

import { useEffect, useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SubscriptionPlan } from '../interfaces/subscriptions';
import { ShieldCheck } from 'lucide-react';

interface SubscribeModalProps {
  plan: SubscriptionPlan | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (email: string) => Promise<void>;
  isLoading?: boolean;
  /** Monto flexible cuando no hay plan fijo */
  flexibleAmount?: number;
  currency?: string;
}

const planCurrencyFormatters = new Map<string, Intl.NumberFormat>();
function getPlanCurrencyFormatter(currency: string) {
  if (!planCurrencyFormatters.has(currency)) {
    planCurrencyFormatters.set(
      currency,
      new Intl.NumberFormat('es-CL', { style: 'currency', currency }),
    );
  }
  return planCurrencyFormatters.get(currency)!;
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function getFrequencyText(plan: SubscriptionPlan) {
  const freq = plan.frequency === 1 ? '' : plan.frequency;
  const type = {
    days: 'días',
    months: plan.frequency === 1 ? 'mes' : 'meses',
    years: plan.frequency === 1 ? 'año' : 'años',
  }[plan.frequency_type];
  return `${freq} ${type}`.trim();
}

export function SubscribeModal({
  plan,
  open,
  onClose,
  onConfirm,
  isLoading,
  flexibleAmount,
  currency = 'CLP',
}: SubscribeModalProps) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (!open) {
      setEmail('');
      setEmailError('');
    }
  }, [open]);

  if (!plan && !flexibleAmount) return null;

  const emailValid = isValidEmail(email);
  const formatter = getPlanCurrencyFormatter(plan?.currency ?? currency);
  const priceLabel = plan
    ? `${formatter.format(Number(plan.price) || 0)} / ${getFrequencyText(plan)}`
    : flexibleAmount
      ? `${formatter.format(flexibleAmount)} / mes`
      : '';

  const handleSubmit = async () => {
    if (!emailValid) {
      setEmailError('Ingresa un email válido');
      return;
    }
    setEmailError('');
    await onConfirm(email.trim());
  };

  return (
    <CustomModal
      isOpen={open}
      onClose={onClose}
      title={plan ? `Unirte a ${plan.name}` : 'Confirmar tu apoyo'}
      description="Serás redirigido a Mercado Pago para completar la suscripción de forma segura."
    >
      <div className="space-y-5">
        <div
          className="rounded-2xl border p-4"
          style={{
            borderColor: 'color-mix(in srgb, var(--subs-accent) 28%, transparent)',
            background:
              'linear-gradient(135deg, color-mix(in srgb, var(--subs-accent) 10%, transparent), transparent 70%)',
          }}
        >
          {plan ? (
            <>
              <p className="font-semibold text-lg">{plan.name}</p>
              {plan.description && (
                <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
              )}
            </>
          ) : (
            <p className="font-semibold text-lg">Aporte mensual flexible</p>
          )}
          <p
            className="text-2xl font-bold mt-3 tracking-tight"
            style={{ color: 'var(--subs-accent)' }}
          >
            {priceLabel}
          </p>
          {plan && plan.trial_days > 0 && (
            <p className="text-xs mt-2 text-amber-400/90">
              Incluye {plan.trial_days} días de prueba gratis
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sub-email">Email</Label>
          <Input
            id="sub-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            placeholder="tu@email.com"
            aria-invalid={!!emailError}
            autoFocus
          />
          {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          <p className="text-xs text-muted-foreground">
            Usaremos este email para gestionar tu suscripción y confirmaciones de pago.
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-card/40 px-3 py-2.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-[var(--subs-accent)]" />
          Pago procesado por Mercado Pago · Cancela cuando quieras
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            className="flex-1 text-white hover:opacity-90"
            style={{ background: 'var(--subs-accent)' }}
            disabled={isLoading || !emailValid}
            onClick={() => void handleSubmit()}
          >
            {isLoading ? 'Procesando...' : 'Ir a Mercado Pago'}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
